import { useEffect, useMemo, useState } from 'react';
import { Transaction, VersionedTransaction } from '@solana/web3.js';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { buildSwapTransaction, getSwapQuote, listPools } from '../../services/OrcaService';
import { confirmTransactionRobust, connectionForTransactions } from '../../services/SolanaRpcService';
import { buildTxSuccessMessage, formatSolanaError } from '../../utils/solanaTxFeedback';
import './Orca.css';

const SOL_MINT = 'So11111111111111111111111111111111111111112';
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const DEFAULT_TOKENS = [
    { mint: SOL_MINT, symbol: 'SOL', name: 'Solana', decimals: 9 },
    { mint: USDC_MINT, symbol: 'USDC', name: 'USD Coin', decimals: 6 }
];

function OrcaSwap() {
    const { connection } = useConnection();
    const { connected, publicKey, wallet, signTransaction } = useWallet();
    const [popularPools, setPopularPools] = useState([]);
    const [tokenMap, setTokenMap] = useState(() => mapTokens(DEFAULT_TOKENS));
    const [selectedPool, setSelectedPool] = useState(null);
    const [form, setForm] = useState({
        inputMint: SOL_MINT,
        outputMint: USDC_MINT,
        inputAmount: '',
        slippageBps: '100'
    });
    const [tokenSearch, setTokenSearch] = useState({ input: '', output: '' });
    const [tokenResults, setTokenResults] = useState({ input: [], output: [] });
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        loadInitialData();
    }, []);

    const tokens = useMemo(() => Object.values(tokenMap).sort(sortTokens), [tokenMap]);
    const inputToken = tokenMap[form.inputMint] || { mint: form.inputMint };
    const outputToken = tokenMap[form.outputMint] || { mint: form.outputMint };

    async function loadInitialData() {
        setLoading('init');
        try {
            const pools = await listPools({ limit: 20 });
            setPopularPools(pools);
            mergeTokens(extractTokensFromPools(pools));
        } catch (err) {
            showError(err);
        } finally {
            setLoading('');
        }
    }

    function mergeTokens(items = []) {
        setTokenMap(current => ({
            ...current,
            ...mapTokens(items)
        }));
    }

    function updateField(event) {
        const { name, value } = event.target;
        setForm(current => ({
            ...current,
            [name]: value
        }));
        setQuote(null);
    }

    function swapSides() {
        setForm(current => ({
            ...current,
            inputMint: current.outputMint,
            outputMint: current.inputMint
        }));
        setSelectedPool(null);
        setQuote(null);
    }

    async function searchTokens(side) {
        const search = tokenSearch[side].trim();
        if (!search) {
            setTokenResults(current => ({ ...current, [side]: [] }));
            return;
        }

        setLoading(`${side}Search`);
        try {
            const pools = await listPools({ limit: 20, search });
            const foundTokens = extractTokensFromPools(pools)
                .filter(token => tokenMatchesSearch(token, search))
                .slice(0, 10);
            mergeTokens(foundTokens);
            setTokenResults(current => ({ ...current, [side]: foundTokens }));
            if (!foundTokens.length) {
                setNotification({
                    type: 'warning',
                    text: 'Nenhuma moeda encontrada em pools para essa busca.'
                });
            }
        } catch (err) {
            showError(err);
        } finally {
            setLoading('');
        }
    }

    function selectToken(side, token) {
        const mint = token.mint;
        setForm(current => ({
            ...current,
            [side === 'input' ? 'inputMint' : 'outputMint']: mint
        }));
        mergeTokens([token]);
        setSelectedPool(null);
        setQuote(null);
    }

    async function findBestPool(inputMint = form.inputMint, outputMint = form.outputMint) {
        if (!inputMint || !outputMint || inputMint === outputMint) return null;
        if (selectedPool && poolHasBothTokens(selectedPool, inputMint, outputMint)) return selectedPool;

        const pools = await listPools({
            limit: 10,
            tokensBothOf: `${inputMint},${outputMint}`
        });
        mergeTokens(extractTokensFromPools(pools));
        const [pool] = pools
            .filter(item => poolHasBothTokens(item, inputMint, outputMint))
            .sort((left, right) => Number(right.tvlUsdc || 0) - Number(left.tvlUsdc || 0));
        setSelectedPool(pool || null);
        return pool || null;
    }

    async function onQuote() {
        if (!form.inputMint || !form.outputMint || !form.inputAmount) {
            setNotification({ type: 'error', text: 'Selecione as moedas e informe a quantidade.' });
            return;
        }
        if (form.inputMint === form.outputMint) {
            setNotification({ type: 'error', text: 'Escolha moedas diferentes para o swap.' });
            return;
        }

        setLoading('quote');
        try {
            const pool = await findBestPool();
            if (!pool) {
                setQuote(null);
                setNotification({
                    type: 'error',
                    text: 'Não encontrei uma pool direta para esse par.'
                });
                return;
            }

            const data = await getSwapQuote({
                walletPublicKey: publicKey ? publicKey.toBase58() : undefined,
                poolAddress: pool.address,
                inputMint: form.inputMint,
                inputAmount: form.inputAmount,
                slippageBps: form.slippageBps
            });
            setQuote(data);
            setNotification({ type: 'success', text: 'Cotação calculada.' });
        } catch (err) {
            showError(err);
        } finally {
            setLoading('');
        }
    }

    async function onSwap() {
        if (!connected || !publicKey) {
            setNotification({ type: 'error', text: 'Conecte a carteira antes do swap.' });
            return;
        }
        if (!signTransaction) {
            setNotification({ type: 'error', text: 'Sua carteira não expôs assinatura de transação.' });
            return;
        }

        setLoading('swap');
        try {
            const pool = await findBestPool();
            if (!pool) {
                setNotification({ type: 'error', text: 'Não encontrei uma pool direta para esse par.' });
                return;
            }

            const built = await buildSwapTransaction({
                walletPublicKey: publicKey.toBase58(),
                poolAddress: pool.address,
                inputMint: form.inputMint,
                inputAmount: form.inputAmount,
                slippageBps: form.slippageBps
            });
            const bytes = Uint8Array.from(atob(built.transaction), c => c.charCodeAt(0));
            const transaction = built.versioned ? VersionedTransaction.deserialize(bytes) : Transaction.from(bytes);
            const signed = await signTransaction(transaction);
            const rpcUrl = process.env.REACT_APP_SOLANA_RPC_URL || connection.rpcEndpoint;
            const rpc = connectionForTransactions(rpcUrl);
            const signature = await rpc.sendRawTransaction(signed.serialize(), {
                skipPreflight: false,
                maxRetries: 3
            });
            const { recoveredAfterTimeout } = await confirmTransactionRobust(rpc, signature);
            setQuote({ ...built, signature });
            setNotification({
                type: 'success',
                text: buildTxSuccessMessage({
                    title: 'Swap concluído com sucesso.',
                    signatures: [signature],
                    anyRecoveredAfterTimeout: recoveredAfterTimeout
                })
            });
        } catch (err) {
            showError(err);
        } finally {
            setLoading('');
        }
    }

    function showError(err) {
        console.error(err);
        setNotification({ type: 'error', text: formatSolanaError(err) });
    }

    function tokenIcon(token) {
        if (token?.imageUrl) return <img src={token.imageUrl} alt={tokenLabel(token)} />;
        return <span>{(tokenLabel(token) || '?').slice(0, 1)}</span>;
    }

    function renderTokenSelector(side, label, selectedToken) {
        const results = tokenResults[side].length ? tokenResults[side] : tokens.slice(0, 8);
        return (
            <div className="orca-token-search">
                <label>{label}</label>
                <div className="orca-token-selected">
                    <div className="orca-token-icon">{tokenIcon(selectedToken)}</div>
                    <div>
                        <strong>{tokenLabel(selectedToken)}</strong>
                        <small>{selectedToken?.name || shortAddress(selectedToken?.mint)}</small>
                    </div>
                </div>
                <div className="orca-token-search-row">
                    <input
                        className="form-control"
                        value={tokenSearch[side]}
                        onChange={event => setTokenSearch(current => ({ ...current, [side]: event.target.value }))}
                        onKeyDown={event => {
                            if (event.key === 'Enter') searchTokens(side);
                        }}
                        placeholder="Buscar símbolo, nome ou mint"
                    />
                    <button className="orca-ghost-btn" onClick={() => searchTokens(side)} disabled={loading === `${side}Search`}>
                        {loading === `${side}Search` ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>
                <div className="orca-token-options">
                    {results.map(token => (
                        <button
                            key={`${side}-${token.mint}`}
                            className={selectedToken?.mint === token.mint ? 'active' : ''}
                            onClick={() => selectToken(side, token)}
                        >
                            {tokenLabel(token)}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <>
            <Menu />
            <main className="content orca-page">
                <div className="orca-container orca-swap-container">
                    <section className="orca-hero swap-hero">
                        <div className="orca-hero-copy">
                            <span className="orca-eyebrow">Swap</span>
                            <h1>Swap entre moedas</h1>
                            <p>Escolha qualquer par disponível nas pools. A melhor pool direta é selecionada automaticamente.</p>
                        </div>
                        <div className="orca-wallet-panel">
                            <WalletMultiButton />
                            <div className="orca-wallet-status">
                                <strong>{connected ? 'Carteira conectada' : 'Conectar carteira'}</strong>
                                <span>{connected && publicKey ? shortAddress(publicKey.toBase58()) : 'Phantom, Solflare ou WalletConnect'}</span>
                                {wallet?.adapter?.name ? <small>{wallet.adapter.name}</small> : <small>Selecione sua carteira.</small>}
                            </div>
                        </div>
                    </section>

                    <section className="orca-swap-layout">
                        <aside className="orca-market-card">
                            <div className="orca-section-header compact">
                                <div>
                                    <h2>Pools populares</h2>
                                    <p>Atalhos para moedas com liquidez nas pools.</p>
                                </div>
                            </div>
                            <div className="orca-pool-list compact-list">
                                {popularPools.map(pool => (
                                    <button className="orca-pool-row" key={pool.address} onClick={() => {
                                        mergeTokens(extractTokensFromPools([pool]));
                                        setForm(current => ({
                                            ...current,
                                            inputMint: pool.tokenA.mint,
                                            outputMint: pool.tokenB.mint
                                        }));
                                        setSelectedPool(pool);
                                        setQuote(null);
                                    }}>
                                        <div className="orca-token-pair">
                                            <div className="orca-token-icons">
                                                <div className="orca-token-icon">{tokenIcon(displayToken(pool, 'tokenA'))}</div>
                                                <div className="orca-token-icon second">{tokenIcon(displayToken(pool, 'tokenB'))}</div>
                                            </div>
                                            <div>
                                                <strong>{tokenLabel(displayToken(pool, 'tokenA'))} / {tokenLabel(displayToken(pool, 'tokenB'))}</strong>
                                                <small>{shortAddress(pool.address)}</small>
                                            </div>
                                        </div>
                                        <div className="orca-pool-metric">
                                            <span>{formatCurrency(pool.tvlUsdc)}</span>
                                            <small>TVL</small>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </aside>

                        <section className="orca-swap-card">
                            {renderTokenSelector('input', 'Você paga', inputToken)}
                            <label className="orca-swap-input-card">
                                <span>Quantidade</span>
                                <input name="inputAmount" value={form.inputAmount} onChange={updateField} placeholder="0.00" type="number" />
                                <em>{tokenLabel(inputToken)}</em>
                            </label>

                            <button className="orca-swap-arrow" onClick={swapSides} title="Inverter moedas" type="button">↓</button>

                            {renderTokenSelector('output', 'Você recebe', outputToken)}
                            <div className="orca-swap-input-card readonly">
                                <span>Estimado</span>
                                <strong>{quote?.estimatedAmountOut?.ui || '0.00'}</strong>
                                <em>{tokenLabel(outputToken)}</em>
                            </div>

                            <div className="orca-form-grid">
                                <div className="orca-field">
                                    <label>Slippage (bps)</label>
                                    <input className="form-control" name="slippageBps" value={form.slippageBps} onChange={updateField} type="number" />
                                </div>
                                <div className="orca-field">
                                    <label>Pool selecionada</label>
                                    <input className="form-control" value={selectedPool ? shortAddress(selectedPool.address) : 'Automática'} readOnly />
                                </div>
                            </div>

                            {selectedPool && (
                                <p className="orca-muted balance-debug">
                                    Pool: {tokenLabel(displayToken(selectedPool, 'tokenA'))} / {tokenLabel(displayToken(selectedPool, 'tokenB'))}
                                    {' · '}
                                    TVL {formatCurrency(selectedPool.tvlUsdc)}
                                </p>
                            )}

                            <div className="orca-actions stacked">
                                <button className="orca-primary-btn wide" disabled={loading === 'quote'} onClick={onQuote}>
                                    {loading === 'quote' ? 'Cotando...' : 'Cotar'}
                                </button>
                                <button className="orca-danger-btn wide" disabled={!quote || loading === 'swap'} onClick={onSwap}>
                                    {loading === 'swap' ? 'Enviando...' : 'Swap'}
                                </button>
                            </div>
                        </section>
                    </section>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

function extractTokensFromPools(pools = []) {
    return pools.flatMap(pool => [displayToken(pool, 'tokenA'), displayToken(pool, 'tokenB')]).filter(Boolean);
}

function mapTokens(tokens = []) {
    return tokens.reduce((map, token) => {
        if (!token?.mint) return map;
        map[token.mint] = token;
        return map;
    }, {});
}

function sortTokens(left, right) {
    const priority = { SOL: 4, USDC: 3, USDT: 2, ORCA: 1 };
    return (priority[tokenLabel(right)] || 0) - (priority[tokenLabel(left)] || 0)
        || tokenLabel(left).localeCompare(tokenLabel(right));
}

function displayToken(pool, side) {
    return pool?.display?.[side] || pool?.[side];
}

function poolHasBothTokens(pool, inputMint, outputMint) {
    const mints = [pool?.tokenA?.mint, pool?.tokenB?.mint];
    return mints.includes(inputMint) && mints.includes(outputMint);
}

function tokenMatchesSearch(token, search) {
    const normalized = search.toLowerCase();
    return [
        token.symbol,
        token.name,
        token.mint
    ].filter(Boolean).some(value => String(value).toLowerCase().includes(normalized));
}

function tokenLabel(token) {
    return token?.symbol || shortAddress(token?.mint);
}

function shortAddress(address) {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function formatCurrency(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '$ -';
    return parsed.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

export default OrcaSwap;
